'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Select } from 'foundations-components/transitional/components/Select';
import { fromJS } from 'immutable';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import ResolveReferences from 'reference-resolvers/ResolveReferences';
import { DEAL_PIPELINE } from 'reference-resolvers/constants/ReferenceObjectTypes';
import { isError, isLoading } from 'reference-resolvers/utils';
import { DEALS } from 'reporting-data/constants/dataTypes';
import { getDataType } from 'reporting-data/report/configReportGetters';
import { getPipelineId } from 'reporting-data/report/funnelReportGetters';
import { isFunnelReport } from 'reporting-data/tsTypes/reportTypes';
import { EVENTS } from 'reporting-ui-components/lib/events';
import useCloneTracker from 'reporting-ui-components/lib/useCloneTracker';
import { ReportType } from 'reporting-ui-components/types/ReportTypes';
import setIn from 'transmute/setIn';
import BaseSelectEditorComponent from './common/BaseSelectEditorComponent';
import { jsx as _jsx } from "react/jsx-runtime";
const setPipeline = setIn(['config', 'pipeline']);
const formatPipeline = pipeline => {
  return {
    text: pipeline.get('label'),
    value: pipeline.get('id')
  };
};
const FunnelPipelineEditorView = ({
  appUsageTracker,
  isV2FilterExperience,
  onChange,
  options,
  pipelines,
  report,
  trackingApplicationName,
  trackingAssetType,
  trackingScreenName
}) => {
  const usageTracker = useCloneTracker(appUsageTracker);
  const handleChange = useCallback(({
    target: {
      value: newPipeline
    }
  }) => {
    usageTracker.track(EVENTS.changeSrvObjectSpecificFilters, {
      filterType: newPipeline,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
    onChange(setPipeline(fromJS({
      id: newPipeline,
      stages: []
    }), report));
  }, [isV2FilterExperience, onChange, report, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker]);
  if (isLoading(pipelines)) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered"
    });
  }
  if (isError(pipelines)) {
    throw pipelines.reason;
  }
  const label = /*#__PURE__*/_jsx(FormattedMessage, {
    message: 'settingLabels.pipeline.label'
  });
  const readOnly = options.readOnly;
  if (isV2FilterExperience) {
    return /*#__PURE__*/_jsx(BaseSelectEditorComponent, {
      tooltipHeader: label,
      options: pipelines.map(formatPipeline).toJS(),
      disabled: readOnly,
      value: getPipelineId(report) || 'default',
      onChange: handleChange
    });
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: label,
    children: /*#__PURE__*/_jsx(Select, {
      buttonUse: readOnly ? 'unstyled' : 'form',
      disabled: readOnly,
      options: pipelines.map(formatPipeline).toJS(),
      value: getPipelineId(report) || 'default',
      onChange: handleChange
    })
  });
};
FunnelPipelineEditorView.propTypes = {
  report: ReportType.isRequired,
  onChange: PropTypes.func.isRequired,
  appUsageTracker: PropTypes.object,
  options: PropTypes.shape({
    readOnly: PropTypes.bool
  }).isRequired,
  pipelines: PropTypes.object.isRequired,
  trackingApplicationName: PropTypes.string,
  trackingAssetType: PropTypes.string,
  trackingScreenName: PropTypes.string,
  isV2FilterExperience: PropTypes.bool
};
const mapResolversToProps = resolvers => {
  return {
    pipelines: resolvers[DEAL_PIPELINE].all()
  };
};
const FunnelPipelineEditor = ResolveReferences(mapResolversToProps)(FunnelPipelineEditorView);
FunnelPipelineEditor.isUsableForReport = report => {
  const dataType = getDataType(report);
  return dataType === DEALS && isFunnelReport(report);
};
export default FunnelPipelineEditor;